import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, HL_KIND } from "@/lib/api";
import { FlagChip, Empty, Chip } from "@/components/ui-bits";
import { MonthlyReport } from "@/components/MonthlyReport";
import { VoiceRecorder, VoicePlayer } from "@/components/VoiceRecorder";

export default function Reports() {
  const [reports, setReports] = useState(null);
  const [view, setView] = useState(() => (window.location.hash === "#monthly" ? "monthly" : "weekly"));
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [note, setNote] = useState("");
  const [voice, setVoice] = useState(null);
  const load = useCallback(() => api.get("/reports").then((r) => setReports(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const saveNote = async (id) => {
    setBusy(true);
    try { await api.patch(`/reports/${id}`, { coachNote: note, voiceNoteId: voice?.id || null }); toast.success(note.trim() || voice ? "Note saved and sent to the client" : "Note removed"); setEditing(null); setVoice(null); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const generate = async () => {
    setBusy(true);
    try { const r = await api.post("/automations/weeklyReports/run"); toast.success(`${r.data.result.reports} reports generated for this week`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!reports) return <div className="center"><span className="spin" /></div>;
  const byClient = new Map();
  for (const r of reports) byClient.set(r.clientId, [...(byClient.get(r.clientId) || []), r]);
  const weekNo = (ws) => Math.floor((Date.now() - new Date(ws).getTime()) / (7 * 86400000));

  return (
    <div className="fade" data-testid="admin-reports">
      <div className="tabs" style={{ marginBottom: 12 }} data-testid="reports-tabs">
        <button className={view === "weekly" ? "on" : ""} onClick={() => { setView("weekly"); window.location.hash = ""; }} data-testid="reports-tab-weekly">Weekly</button>
        <button className={view === "monthly" ? "on" : ""} onClick={() => { setView("monthly"); window.location.hash = "monthly"; }} data-testid="reports-tab-monthly">Monthly report card</button>
      </div>
      {view === "monthly" && <MonthlyReport />}
      {view === "weekly" && <div className="grid2">
      <div className="panel">
        <div className="ph"><h3>Weekly summaries · archived &amp; immutable</h3>
          <button className="btn sm" disabled={busy} onClick={generate} data-testid="generate-reports-button">Generate this week</button></div>
        {reports.length === 0 && <Empty>No reports yet.</Empty>}
        {reports.slice(0, 40).map((r) => (
          <div className="rep" key={r.id} style={{ alignItems: "flex-start" }} data-testid={`report-${r.id}`}>
            <div className="rd num">W-{weekNo(r.weekStart)} · {fmtDate(r.weekStart)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="st" style={{ fontSize: 13 }}>{r.clientName}</div>
              <div className="ss">{r.summary}</div>
              {(r.highlights || []).length > 0 && <div className="hl" data-testid={`report-highlights-${r.id}`}>{r.highlights.map((h, i) => <Chip key={i} cls={HL_KIND[h.kind]?.[0]} label={h.text} />)}</div>}
              {editing === r.id ? (
                <div style={{ marginTop: 6 }}>
                  <textarea rows={2} autoFocus value={note} onChange={(e) => setNote(e.target.value)} placeholder="Personal note for the client…" data-testid={`note-input-${r.id}`} />
                  <VoiceRecorder value={voice} onChange={setVoice} />
                  <div className="actions" style={{ marginTop: 6 }}>
                    <button className="btn sm" disabled={busy} onClick={() => saveNote(r.id)} data-testid={`note-save-${r.id}`}>Save &amp; send</button>
                    <button className="btn sm g" onClick={() => setEditing(null)} data-testid={`note-cancel-${r.id}`}>Cancel</button>
                  </div>
                </div>
              ) : (r.coachNote || r.voiceNoteId) ? (
                <div className="note" onClick={() => { setEditing(r.id); setNote(r.coachNote || ""); setVoice(r.voiceNoteId ? { id: r.voiceNoteId, url: `${process.env.REACT_APP_BACKEND_URL}/api/public/voice/${r.voiceNoteId}`, durationSec: "–" } : null); }} data-testid={`note-${r.id}`}><b>Coach note:</b> {r.coachNote || "(voice note)"} <span className="ss">· edit</span>
                  {r.voiceNoteId && <div onClick={(e) => e.stopPropagation()}><VoicePlayer id={r.voiceNoteId} testId={`report-voice-${r.id}`} /></div>}</div>
              ) : (
                <button className="linkbtn" onClick={() => { setEditing(r.id); setNote(""); setVoice(null); }} data-testid={`note-add-${r.id}`}>+ Add personal note</button>
              )}
            </div>
            <div className="rbar"><i style={{ width: `${r.adherence}%` }} /></div>
            <div className="rv num">{r.adherence}%</div>
            <FlagChip flag={r.flag} />
          </div>
        ))}
      </div>
      <div className="panel">
        <h3>Performance indicators · avg adherence</h3>
        {[...byClient.entries()].sort((a, b) => a[1][0].clientName.localeCompare(b[1][0].clientName)).map(([cid, rs]) => {
          const perf = Math.round(rs.reduce((a, r) => a + r.adherence, 0) / rs.length);
          return (
            <div className="rep" key={cid} data-testid={`perf-${cid}`}>
              <div style={{ width: 130, fontSize: 13, fontWeight: 600 }}>{rs[0].clientName}</div>
              <div className="rbar"><i style={{ width: `${perf}%`, background: perf < 50 ? "var(--bad)" : perf < 75 ? "var(--warn)" : "var(--ink)" }} /></div>
              <div className="rv num">{perf}</div>
              <div className="ss" style={{ width: 60, textAlign: "right" }}>{rs.length} wks</div>
            </div>
          );
        })}
      </div>
      </div>}
    </div>
  );
}
